import {HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {AnyAdapter} from '@drl/core/api/adapters/any-adapter.service';
import {ApiService} from '@drl/core/api/api.service';
import {ValueResponse} from '@drl/shared/models';

import {
  GetMedicineLogCommand,
  GetReportedSymptomsCommand,
  GetTherapyAdherenceCommand,
} from './shared/commands';
import {MedicineLog} from './shared/models';
import {GetTreatmentAdherenceCommand} from './shared/commands/get-treatment-adherence.command';
import {GetHealthQueryCommand} from './shared/commands/get-health-queries.command';
import {
  GetActivePresStatus,
  GetRefillRate,
} from '../care-central/commands/presc-refill.commands';
import {AddRxStatusCard, RefillRate} from '../care-central/models/refill.model';

@Injectable()
export class HealthMetricsService {
  constructor(
    private readonly apiService: ApiService,
    private readonly anyAdapter: AnyAdapter,
  ) {}

  getReportedSymptomsCount(patId: number, month: number, year: number) {
    const command: GetReportedSymptomsCommand<ValueResponse> =
      new GetReportedSymptomsCommand(this.apiService, this.anyAdapter, patId);

    let params = new HttpParams();
    params = params.set('month', month.toString());
    params = params.set('year', year.toString());

    command.parameters = {
      query: params,
    };

    return command.execute();
  }

  //Health Queries
  getHealthQueryCount(patId: number, month: number, year: number) {
    const command: GetHealthQueryCommand<ValueResponse> =
      new GetHealthQueryCommand(this.apiService, this.anyAdapter, patId);

    let params = new HttpParams();
    params = params.set('month', month.toString());
    params = params.set('year', year.toString());

    command.parameters = {
      query: params,
    };

    return command.execute();
  }
  //

  getTherapyAdherencePercentage(patId: number, month: number, year: number) {
    const command: GetTherapyAdherenceCommand<ValueResponse> =
      new GetTherapyAdherenceCommand(this.apiService, this.anyAdapter, patId);

    let params = new HttpParams();
    params = params.set('month', month.toString());
    params = params.set('year', year.toString());

    command.parameters = {
      query: params,
    };

    return command.execute();
  }

  getTreatmentAdherencePercentage(patId: number, month: number, year: number) {
    const command: GetTreatmentAdherenceCommand<ValueResponse> =
      new GetTreatmentAdherenceCommand(this.apiService, this.anyAdapter, patId);

    let params = new HttpParams();
    params = params.set('month', month.toString());
    params = params.set('year', year.toString());

    command.parameters = {
      query: params,
    };

    return command.execute();
  }

  getMedicineLogs(
    patientId?: number,
    startDate?: Date,
    endDate?: Date,
    orderBy?: string,
    prescriptionId?: number,
  ) {
    const command: GetMedicineLogCommand<MedicineLog[]> =
      new GetMedicineLogCommand(this.apiService, this.anyAdapter);

    const order = [];
    const queryData = {
      patientId: undefined,
      prescriptionId: undefined,
      and: [],
    };
    if (patientId) {
      queryData.patientId = patientId;
    }
    if (prescriptionId) {
      queryData.prescriptionId = prescriptionId;
    }
    if (startDate) {
      queryData.and.push({
        timeToTake: {
          gte: startDate,
        },
      });
    }
    if (endDate) {
      queryData.and.push({
        timeToTake: {
          lte: endDate,
        },
      });
    }

    if (orderBy) {
      order.push(orderBy);
    } else {
      order.push('medicineId ASC');
    }
    const include = [{relation: 'prescriptionMedicines'}];
    const queryStr = JSON.stringify({
      where: queryData,
      include,
      order,
    });
    let params = new HttpParams();
    params = params.set('filter', queryStr);

    //setting timezone
    if (Intl.DateTimeFormat().resolvedOptions().timeZone != 'Etc/Unknown') {
      params = params.set(
        'tz',
        Intl.DateTimeFormat().resolvedOptions().timeZone,
      );
    } else {
      // default time zone in brazil -3 if system unable to detect timezone
      params = params.set('tz', 'America/Bahia');
    }
    command.parameters = {
      query: params,
    };

    return command.execute();
  }

  getRefillRate(patId: number) {
    const command: GetRefillRate<RefillRate> = new GetRefillRate(
      this.apiService,
      this.anyAdapter,
      patId,
    );

    return command.execute();
  }

  getActivePresSatus(patId: number) {
    const command: GetActivePresStatus<AddRxStatusCard> =
      new GetActivePresStatus(this.apiService, this.anyAdapter, patId);

    let params = new HttpParams();
    if (Intl.DateTimeFormat().resolvedOptions().timeZone != 'Etc/Unknown') {
      params = params.set(
        'tz',
        Intl.DateTimeFormat().resolvedOptions().timeZone,
      );
    } else {
      // default time zone in brazil -3 if system unable to detect timezone
      params = params.set('tz', 'America/Bahia');
    }
    command.parameters = {
      query: params,
    };

    return command.execute();
  }
}
